/**
 * Safe storage write helpers — catch QuotaExceededError to prevent app crashes.
 * Includes storage pressure detection and emergency cleanup.
 */

// ── Non-essential keys that can be pruned under pressure ──
const PRUNABLE_KEYS = [
  'aphenis_crash_log',
  'aphenis_petworld_pois_discovered',
  'aphenis_petworld_fish_caught',
  'aphenis_petworld_identified_mats',
  'aphenis_petworld_biomes_visited',
  'aphenis_petworld_craft_count',
  'aphenis_petworld_seen_recipes',
  'aphenis_petworld_dailies',
  'aphenis_petworld_daily_streak',
  'aphenis_petworld_gardens',
  'aphenis_petworld_city_buffs',
  'aphenis_guardian_alerts',
  'aphenis_xp_log',
  'aphenis_xp_log_sig',
  'aphenis_game_history',
  'aphenis_game_history_sig',
  'aphenis_voice_transcriptions',
  'aphenis_pet_activity_feed',
  'aphenis_pet_activity_log',
  'aphenis_notification_center',
  'aphenis_petworld_checkpoint',
];

/** Estimate localStorage usage in bytes */
export function getStorageUsageBytes(): number {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const val = localStorage.getItem(key);
        // Each JS char = 2 bytes in storage
        total += (key.length + (val?.length || 0)) * 2;
      }
    }
  } catch {}
  return total;
}

/** Get storage pressure as 0-1 ratio (1 = 5MB limit) */
export function getStoragePressure(): number {
  const LIMIT = 5 * 1024 * 1024; // 5MB
  return Math.min(getStorageUsageBytes() / LIMIT, 1);
}

/** Prune non-critical data to free space. Returns bytes freed (approx). */
export function emergencyCleanup(): number {
  let freed = 0;
  for (const key of PRUNABLE_KEYS) {
    try {
      const val = localStorage.getItem(key);
      if (val) {
        freed += (key.length + val.length) * 2;
        localStorage.removeItem(key);
      }
    } catch {}
  }
  // Also prune any stale prefixed keys
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('aphenis_petworld_purchased_') ||
        key.startsWith('aphenis_merchant_purchased_') ||
        key.startsWith('aphenis_pet_last_interactions_') ||
        key.startsWith('aphenis_pet_interaction_counts_')
      )) {
        stale.push(key);
      }
    }
    for (const key of stale.slice(0, 30)) {
      const val = localStorage.getItem(key);
      if (val) freed += (key.length + val.length) * 2;
      localStorage.removeItem(key);
    }
  } catch {}
  return freed;
}

/** Auto-prune if pressure exceeds 80% */
function autoPruneIfNeeded(): void {
  if (getStoragePressure() > 0.8) {
    emergencyCleanup();
  }
}

/** Safely write a string value to localStorage. Returns true on success. */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`[Storage] Failed to write '${key}' — attempting cleanup:`, e);
    // Attempt emergency cleanup and retry once
    try {
      emergencyCleanup();
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }
}

/** Safely write a JSON-serializable value to localStorage. Returns true on success. */
export function safeSetJSON(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.warn(`[Storage] Failed to write JSON '${key}':`, e);
    try {
      emergencyCleanup();
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }
}

/** Safely write a string value to sessionStorage. Returns true on success. */
export function safeSessionSetItem(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`[Storage] Failed to write session '${key}':`, e);
    return false;
  }
}

// ── Prefixes whose keys are safe to remove on login ──
// IMPORTANT: pet needs interaction timestamps & init flags MUST persist across login,
// otherwise the pet is reset to a partly-drained state on every login.
const STALE_PREFIXES = [
  'aphenis_merchant_purchased_',
  'aphenis_petworld_purchased_',
  // 'aphenis_pet_last_interactions_' — REMOVED: needs survive login
  'aphenis_pet_interaction_counts_',
];

// ── Prefixes that MUST be preserved across login cleanup ──
const PRESERVE_PREFIXES = [
  'aphenis_identity',
  'aphenis_keys',
  'aphenis_unlocked',
  'aphenis_unlock_code',
  'aphenis_local_verification',
  'aphenis_settings',
  'aphenis_theme',
  'aphenis_bubble_style',
  'aphenis_chat_theme',
  'aphenis_pet_species',
  'aphenis_chat_pets',
  'aphenis_primary_pet',
  'aphenis_pet_lifecycle',
  'aphenis_pet_needs',
  'aphenis_contacts',
  'aphenis_duress_pin',
  'aphenis_auto_biometrics',
  'aphenis_passkey',
  'aphenis_guardian_fingerprint',
  'aphenis_device_fingerprint',
  'aphenis_color_mode',
  'aphenis_language',
  'aphenis_font_size',
  'aphenis_ghost_mode',
  'aphenis_decoy',
  'aphenis_version',
  'aphenis_onboarding',
  'aphenis_first_login',
  'aphenis_pet_system_onboarding',
  'aphenis_whats_new',
];

/**
 * Remove non-essential cached data from localStorage on login.
 * Preserves identity, keys, settings, and core pet state.
 * Everything removed rebuilds itself on use.
 */
export function loginCleanup(): void {
  let removed = 0;
  let freedBytes = 0;

  // First remove exact PRUNABLE_KEYS
  for (const key of PRUNABLE_KEYS) {
    try {
      const val = localStorage.getItem(key);
      if (val) {
        freedBytes += (key.length + val.length) * 2;
        localStorage.removeItem(key);
        removed++;
      }
    } catch {}
  }

  // Then iterate all keys and remove stale-prefixed ones
  // (iterate in reverse to avoid index shifting)
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (STALE_PREFIXES.some(p => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      const val = localStorage.getItem(key);
      if (val) freedBytes += (key.length + val.length) * 2;
      localStorage.removeItem(key);
      removed++;
    }
  } catch {}

  if (removed > 0) {
    console.log(`[LoginCleanup] Removed ${removed} keys, freed ~${(freedBytes / 1024).toFixed(1)}KB`);
  }
}

// Run auto-prune on module load (app startup)
try { autoPruneIfNeeded(); } catch {}
